// Ported from the legacy js/earth.js. Pixel <-> lat/lng math for the
// 100-million-frame world grid that powers the /world page.

export const MAX_X = 20016;
export const MAX_Y = 10019;

// 1km per latitude unit. (Matches the comment in earth.js: the constant was
// briefly set to 1/111.321 — i.e. 1km per "block" — and that's what the
// production maths assume.)
export const LAT_DISTANCE = 1 / (40007.863 / 360);

/**
 * Must match backend `modules/googlemaps.py#block_to_integer` and legacy
 * `main.js#blockToInteger` (adjusted index encodes nonnegative x/y with +1 bump).
 */
export function blockToInteger(x: number, y: number): number {
  const adjustedX =
    x + MAX_X + 1 + (x > 0 || Object.is(x, 0) ? 1 : 0);
  const adjustedY =
    y + MAX_Y + 1 + (y > 0 || Object.is(y, 0) ? 1 : 0);
  return adjustedY * (2 * MAX_X + 3) + adjustedX;
}

/** Inverse of backend `integer_to_block` (mirrors `modules/googlemaps.py`). */
export function integerToBlock(integer: number): [number, number] {
  const adjustedY = Math.floor(integer / (2 * MAX_X + 3));
  const adjustedX = integer % (2 * MAX_X + 3);

  const x =
    (adjustedX - MAX_X - 1 - (adjustedX >= MAX_X + 2 ? 1 : 0)) *
    (adjustedX === MAX_X + 1 ? -1 : 1);
  const y =
    (adjustedY - MAX_Y - 1 - (adjustedY >= MAX_Y + 2 ? 1 : 0)) *
    (adjustedY === MAX_Y + 1 ? -1 : 1);

  return [x, y];
}

export function calculateLngDistance(lat: number, latDistanceKm: number): number {
  const earthRadiusKm = 6371;
  const latRad = lat * (Math.PI / 180);
  const circumferenceAtLat = 2 * Math.PI * earthRadiusKm * Math.cos(latRad);
  return (latDistanceKm / circumferenceAtLat) * 360;
}

/** From legacy main.js#getBlockNumber — grid indices for SW corner lat/lng. */
export function getBlockNumber(lat: number, lng: number): [number, number] {
  let latDistance = LAT_DISTANCE;
  let lngDistance = calculateLngDistance(lat, 1);
  if (lng < 0) lngDistance *= -1;

  let y = Math.round(lat / Math.abs(latDistance));
  let x = Math.round(lng / Math.abs(lngDistance));

  if (lat === 0 && latDistance < 0) y = -0;
  if (lng === 0 && lngDistance < 0) x = -0;

  return [x, y];
}

/** Legacy main.js#handleMapClick — map click → canonical pixel integer. */
export function pixelIntegerFromLatLng(lat: number, lng: number): number {
  let latDistance = LAT_DISTANCE;
  const latStart = lat - (lat % latDistance);
  let lngDistance = calculateLngDistance(latStart, 1);

  let lngStart = lng - (lng % lngDistance);
  if (lat < 0) latDistance *= -1;
  if (lng < 0) lngDistance *= -1;

  const [x, y] = getBlockNumber(latStart, lngStart);
  return blockToInteger(x, y);
}

export function getLatLngFromBlockNumber(
  x: number,
  y: number,
): [number, number] {
  const lat = y * Math.abs(LAT_DISTANCE);
  const lngDistance = calculateLngDistance(lat, 1);
  const lng = x * Math.abs(lngDistance);
  return [lat, lng];
}

export function convertDecimalToDMS(
  decimalDegrees: number,
  type: "lat" | "lng",
): string {
  const degrees = Math.floor(Math.abs(decimalDegrees));
  const minutesDecimal = (Math.abs(decimalDegrees) - degrees) * 60;
  const minutes = Math.floor(minutesDecimal);
  const seconds = (minutesDecimal - minutes) * 60;

  const direction =
    type === "lat"
      ? decimalDegrees >= 0
        ? "N"
        : "S"
      : decimalDegrees >= 0
        ? "E"
        : "W";

  return `${degrees}°${minutes}'${seconds.toFixed(2)}''${direction}`;
}
